<template>
  <Page subpage>
    <div v-if="loading" class="loading">
      <Spinner class="mt-3" message="Loading media..." />
    </div>
    <template v-else>
      <div class="mb-8">
        <PhotoGallery
          scope="customer"
          :customer-id="customerId"
          :photos="photos"
          :upload-link="photoUploadLink"
          @upload-success="fetchMedia"
          @delete-success="fetchMedia"
        />
      </div>
      <FeatureFlag feature="video_uploads">
        <div class="mb-8">
          <VideoGallery readonly :videos="videos" />
        </div>
      </FeatureFlag>
      <Documents
        scope="customer"
        :customer-id="customerId"
        :documents="documents"
        :upload-link="documentUploadLink"
        @upload-success="fetchMedia"
        @delete-success="fetchMedia"
      />
    </template>
  </Page>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import { useStore } from "vuex";

import FeatureFlag from "@/components/FeatureFlag.vue";
import Page from "@/components/Page.vue";
import Spinner from "@/components/Spinner2.vue";
import Documents from "@/Documents/Documents.vue";
import PhotoGallery from "@/PhotoGallery/PhotoGallery.vue";
import { Document } from "@/typings/document";
import { Photo } from "@/typings/photo";
import { Video } from "@/typings/video";
import VideoGallery from "@/VideoGallery/VideoGallery.vue";

const props = withDefaults(
  defineProps<{
    customerId?: number | string | null;
  }>(),
  {
    customerId: null
  }
);

const store = useStore();

const loading = ref(true);
const photos = ref<Photo[]>([]);
const documents = ref<Document[]>([]);
const videos = ref<Video[]>([]);

function reserveLink(kind: "photos" | "documents") {
  return computed(() =>
    props.customerId == null
      ? undefined
      : {
          href: `/v2/customers/${props.customerId}/${kind}/reserve`,
          method: "POST"
        }
  );
}
const photoUploadLink = reserveLink("photos");
const documentUploadLink = reserveLink("documents");

async function fetchMedia() {
  const { customerId } = props;
  if (customerId == null) {
    loading.value = false;
    return;
  }
  loading.value = true;
  try {
    const [photoData, documentData, videoData] = await Promise.all([
      store.dispatch("fetchCustomerMedia", { customerId, type: "photo" }),
      store.dispatch("fetchCustomerMedia", { customerId, type: "document" }),
      store.dispatch("fetchCustomerMedia", { customerId, type: "video" })
    ]);
    photos.value = photoData ?? [];
    documents.value = documentData ?? [];
    videos.value = videoData ?? [];
  } catch {
    window.notify?.error("Failed to load media. Please try again.");
  } finally {
    loading.value = false;
  }
}

onMounted(fetchMedia);
watch(() => props.customerId, fetchMedia);
</script>

<style scoped>
.loading {
  min-height: 150px;
}

/* Match the job Media tab, where each gallery card is borderless. The job page
   gets this from Job/jobs_tailwind.css (not loaded here), so mirror it. */
:deep(.photo-gallery.card) {
  border: none !important;
  box-shadow: none !important;
  padding: 0 !important;
}
</style>
